import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api';
import { ChannelLogo, playLink } from '../components/LiveBits';
import { ClubCard, Crest, FormChips, MatchRow, ScoreBoard, StandingsTable } from '../components/SportCenterBits';
import { Follow, ForYou, Highlights, SportChannels } from './SportHubPage';
import { useHideScores } from '../sporthub';
import { linkProps, navigate } from '../router';
import {
  broadcastsFor, dayRange, dayText, hhmm, loadScLeagues, scApi, setScSettings, tableHref, usePolling, useScSettings,
  type MatchDetail, type ScLeague, type SEventRow, type SMatch,
} from '../sportcenter';
import type { SportEvent } from '../api';

const TABS: [string, string][] = [['voorjou', 'Voor jou'], ['uitslagen', 'Uitslagen'], ['standen', 'Standen'], ['zenders', 'Zenders'], ['samenvattingen', 'Samenvattingen']];
const errText = (e: unknown, fb: string) => (e instanceof ApiError && e.message !== e.code ? e.message : fb);

/** Sports Hub: wat je volgt, uitslagen, standen, sport op je zenders, samenvattingen en volgen. */
export function SportCenterPage({ tab, params }: { tab: string; params: URLSearchParams }) {
  if (tab === 'match') return <MatchPage path={params.get('path') ?? ''} event={params.get('event') ?? ''} />;
  if (tab === 'club') return <ClubPage path={params.get('path') ?? ''} id={params.get('id') ?? ''} />;
  // Oude adressen blijven werken
  const alias: Record<string, string> = { today: 'voorjou', kiezen: 'volgen', clubs: 'voorjou' };
  const t = alias[tab] ?? tab;
  const current = t === 'volgen' ? 'volgen' : TABS.find(([id]) => id === t)?.[0] ?? 'voorjou';
  // "?d=" hoorde bij de oude dagenlijst: dan de uitslagen
  const shown = tab === 'today' && params.get('d') ? 'uitslagen' : current;
  return (
    <main className="page sc-page sh-hub">
      <header className="page-head sh-head">
        <h1>Sport</h1>
        <nav className="filterbar srv-tabs" aria-label="Onderdelen" data-focus-group>
          {TABS.map(([id, label]) => (
            <a key={id} className="chip" aria-pressed={id === shown} aria-current={id === shown ? 'page' : undefined} {...linkProps(id === 'voorjou' ? '/sportcenter' : `/sportcenter/${id}`)}>{label}</a>
          ))}
          <a className="chip" {...linkProps('/live/multi')}>Multiview</a>
        </nav>
        <span className="spacer" />
        <a className="chip" aria-pressed={shown === 'volgen'} {...linkProps('/sportcenter/volgen')}>★ Volgen</a>
      </header>
      {shown === 'voorjou' && <ForYou />}
      {shown === 'uitslagen' && <Today offset={Number(params.get('d')) || 0} />}
      {shown === 'standen' && <Standings league={params.get('league')} />}
      {shown === 'zenders' && <SportChannels />}
      {shown === 'samenvattingen' && <Highlights />}
      {shown === 'volgen' && <Follow />}
      <div className="footer-space" />
    </main>
  );
}

// ---------- Wedstrijden per dag ----------

function Today({ offset }: { offset: number }) {
  const [from, to] = dayRange(offset);
  const [all, setAll] = useState(false);
  const [liveOnly, setLiveOnly] = useState(false);
  const hide = useHideScores();
  const settings = useScSettings();
  const { data, error } = usePolling(`scores:${from}:${all}`, () => scApi.scores(from, to, all));
  const days = [-2, -1, 0, 1, 2, 3, 4, 5, 6];
  const groups = useMemo(() => {
    const list = (data?.matches ?? []).filter((m) => !liveOnly || m.state === 'in');
    const fav = list.filter((m) => m.fav);
    const rest = list.filter((m) => !m.fav);
    const byLeague = new Map<string, SMatch[]>();
    for (const m of rest) {
      const g = byLeague.get(m.leagueName) ?? [];
      g.push(m);
      byLeague.set(m.leagueName, g);
    }
    // Competities met live wedstrijden eerst, daarna in de volgorde van je competities
    const order = settings?.leagues ?? [];
    const out = [...byLeague.entries()].map(([name, ms]) => ({ name, league: ms[0].league, matches: ms.sort((a, b) => a.start - b.start) }));
    out.sort((a, b) => Number(b.matches.some((m) => m.state === 'in')) - Number(a.matches.some((m) => m.state === 'in'))
      || (order.indexOf(a.league) + 1 || 99) - (order.indexOf(b.league) + 1 || 99));
    return { fav: fav.sort((a, b) => a.start - b.start), leagues: out };
  }, [data, settings, liveOnly]);
  return (
    <div className={hide ? 'hide-scores' : undefined}>
      <div className="filterbar sc-days" data-focus-group>
        {days.map((d) => {
          const [f] = dayRange(d);
          return <a key={d} className="chip" aria-pressed={d === offset} {...linkProps(d ? `/sportcenter/uitslagen?d=${d}` : '/sportcenter/uitslagen')}>{dayText(f)}</a>;
        })}
        <span className="chip-sep" />
        <button type="button" className="chip" aria-pressed={liveOnly} onClick={() => setLiveOnly(!liveOnly)}>● Alleen live</button>
        <button type="button" className="chip" aria-pressed={all} onClick={() => setAll(!all)}>Alle competities</button>
      </div>
      {error && !data && <p className="placeholder-row error">{error}</p>}
      {!data && !error && <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />}
      {settings && !settings.teams.length && offset === 0 && (
        <p className="placeholder-row">Kies je clubs bij <a {...linkProps('/sportcenter/volgen')}>Clubs & competities</a>: hun wedstrijden staan dan bovenaan, ook in de beker en Europa.</p>
      )}
      {data && !data.matches.length && <p className="placeholder-row">Geen wedstrijden op {dayText(from).toLowerCase()} in je competities.</p>}
      {groups.fav.length > 0 && (
        <section className="sc-group sc-group-fav">
          <h2>Mijn clubs</h2>
          <div className="sc-list" data-focus-group>{groups.fav.map((m) => <MatchRow key={m.id} m={m} showLeague />)}</div>
        </section>
      )}
      {groups.leagues.map((g) => (
        <section key={g.name} className="sc-group">
          <h2 className="row-title-link"><a {...linkProps(tableHref(g.league))}>{g.name} <span className="row-title-more" aria-hidden="true">›</span></a></h2>
          <div className="sc-list" data-focus-group>{g.matches.map((m) => <MatchRow key={m.id} m={m} />)}</div>
        </section>
      ))}
    </div>
  );
}

// ---------- Mijn clubs ----------


// ---------- Standen ----------

function Standings({ league }: { league: string | null }) {
  const settings = useScSettings();
  const [leagues, setLeagues] = useState<ScLeague[]>([]);
  useEffect(() => {
    void loadScLeagues().then(setLeagues).catch(() => undefined);
  }, []);
  const mine = settings?.leagues ?? [];
  const fromTeams = settings?.teams.map((t) => leagues.find((l) => l.path === t.path)?.id).filter((x): x is string => !!x) ?? [];
  const ordered = [...new Set([...fromTeams, ...mine])];
  const current = league ?? ordered[0] ?? 'eredivisie';
  const [data, setData] = useState<Awaited<ReturnType<typeof scApi.standings>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setData(null);
    setError(null);
    scApi.standings({ league: current }).then(setData).catch((e: unknown) => setError(errText(e, 'De stand is even niet bereikbaar.')));
  }, [current]);
  const fav = new Set(settings?.teams.map((t) => t.id) ?? []);
  const others = leagues.filter((l) => !ordered.includes(l.id));
  return (
    <>
      <div className="filterbar" data-focus-group>
        {ordered.map((id) => {
          const l = leagues.find((x) => x.id === id);
          return l ? <a key={id} className="chip" aria-pressed={id === current} {...linkProps(tableHref(id))}>{l.name}</a> : null;
        })}
        <select className="chip-select" aria-label="Andere competitie" value={others.some((l) => l.id === current) ? current : ''} onChange={(e) => e.target.value && navigate(tableHref(e.target.value))}>
          <option value="">Andere competitie…</option>
          {others.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      {error && <p className="placeholder-row error">{error}</p>}
      {!data && !error && <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />}
      {data && !data.groups.length && <p className="placeholder-row">Voor deze competitie is (nog) geen stand.</p>}
      {data && data.groups.length > 0 && (
        <div className="sc-standings">
          <StandingsTable groups={data.groups} highlight={fav} league={current} path={data.league.path} />
          <Legend groups={data.groups} />
        </div>
      )}
    </>
  );
}

function Legend({ groups }: { groups: Awaited<ReturnType<typeof scApi.standings>>['groups'] }) {
  const notes = new Map<string, string>();
  for (const g of groups) for (const r of g.rows) if (r.note) notes.set(r.note.text, r.note.color);
  if (!notes.size) return null;
  return (
    <ul className="sc-legend">
      {[...notes].map(([t, c]) => <li key={t}><i style={{ background: c }} />{t}</li>)}
    </ul>
  );
}

// ---------- Club ----------

function ClubPage({ path, id }: { path: string; id: string }) {
  const { data, error } = usePolling(`club:${path}:${id}`, () => scApi.team(path, id).then((d) => ({ ...d, poll: d.live ? 20 : 300 })));
  const settings = useScSettings();
  const [table, setTable] = useState<Awaited<ReturnType<typeof scApi.standings>> | null>(null);
  const teamPath = data?.team.path;
  useEffect(() => {
    if (teamPath) scApi.standings({ path: teamPath }).then(setTable).catch(() => setTable(null));
  }, [teamPath]);
  if (error && !data) return <main className="page"><p className="placeholder-row error">{error}</p></main>;
  if (!data) return <main className="page"><div className="row-skeleton" style={{ margin: 'var(--gutter)' }} /></main>;
  const following = settings?.teams.some((t) => t.id === data.team.id && t.path === data.team.path);
  const toggle = async () => {
    if (!settings) return;
    const t = data.team;
    const teams = following ? settings.teams.filter((x) => !(x.id === t.id && x.path === t.path)) : [...settings.teams, { id: t.id, path: t.path, name: t.name, short: t.short, abbr: t.abbr, logo: t.logo, color: t.color }];
    setScSettings(await scApi.save({ teams }));
  };
  return (
    <main className="page sc-page">
      <header className="page-head sc-club-page-head">
        <Crest logo={data.team.logo} name={data.team.short} size={64} />
        <div>
          <span className="eyebrow">{data.team.leagueName}</span>
          <h1>{data.team.name}</h1>
        </div>
        <button type="button" className={`btn btn-sm${following ? '' : ' btn-primary'}`} onClick={() => void toggle()}>{following ? '★ Volg je' : '☆ Volgen'}</button>
      </header>
      <div className="sc-club-layout">
        <ClubCard s={data} />
        <div className="sc-club-side">
          <section className="sc-group">
            <h2>Vorm</h2>
            <div className="sc-pad"><FormChips form={data.form} big /></div>
          </section>
          {table && table.groups.length > 0 && (
            <section className="sc-group">
              <h2 className="row-title-link"><a {...linkProps(tableHref(table.league.id))}>Stand <span className="row-title-more" aria-hidden="true">›</span></a></h2>
              <StandingsTable groups={table.groups} highlight={new Set([data.team.id])} around={data.team.id} league={table.league.id} path={table.league.path} compact />
            </section>
          )}
        </div>
      </div>
      {data.upcoming.length > 0 && (
        <section className="sc-group">
          <h2>Programma</h2>
          <div className="sc-list" data-focus-group>{data.upcoming.map((m) => <MatchRow key={m.id} m={{ ...m, fav: false }} showLeague />)}</div>
        </section>
      )}
      {data.recent.length > 0 && (
        <section className="sc-group">
          <h2>Uitslagen</h2>
          <div className="sc-list" data-focus-group>{data.recent.map((m) => <MatchRow key={m.id} m={m} showLeague />)}</div>
        </section>
      )}
      <div className="footer-space" />
    </main>
  );
}

// ---------- Wedstrijd ----------

function minuteOf(s: string): number {
  const m = s.match(/(\d+)'?(?:\s*\+\s*(\d+))?/);
  return m ? Number(m[1]) + (Number(m[2]) || 0) / 3 : 0;
}

function Timeline({ d }: { d: MatchDetail }) {
  const marks = d.events.filter((e) => e.side && (e.kind === 'goal' || e.kind === 'pen' || e.kind === 'own' || e.kind === 'red'));
  const span = Math.max(95, ...marks.map((e) => minuteOf(e.minute) + 2));
  const now = d.match.state === 'in' && d.match.minute ? minuteOf(d.match.minute) : d.match.state === 'post' ? span : 0;
  return (
    <div className="sc-timeline" aria-label="Verloop">
      <span className="sc-tl-label">{d.match.home.short}</span>
      <div className="sc-tl-bar">
        <i className="sc-tl-progress" style={{ width: `${Math.min(100, (now / span) * 100)}%` }} />
        <i className="sc-tl-half" style={{ left: `${(45 / span) * 100}%` }} />
        {marks.map((e, i) => (
          <span key={i} className={`sc-tl-mark ${e.side === 'home' ? 'is-home' : 'is-away'} sc-tl-${e.kind}`} style={{ left: `${(minuteOf(e.minute) / span) * 100}%` }} title={`${e.minute} ${e.text}${e.player ? ` · ${e.player}` : ''}`}>
            {e.kind === 'red' ? '▮' : '⚽'}
          </span>
        ))}
      </div>
      <span className="sc-tl-label">{d.match.away.short}</span>
    </div>
  );
}

const EVENT_ICON: Record<SEventRow['kind'], string> = { goal: '⚽', pen: '⚽', own: '⚽', yellow: '🟨', red: '🟥', sub: '⇄', period: '', other: '•' };

function MatchPage({ path, event }: { path: string; event: string }) {
  const { data, error } = usePolling(`match:${path}:${event}`, () => scApi.match(path, event).then((d) => ({ ...d, poll: d.match.state === 'in' ? 20 : d.match.state === 'pre' && d.match.start - Date.now() < 3600_000 ? 60 : 600 })));
  const [tab, setTab] = useState<'events' | 'stats' | 'lineups'>('events');
  const [channels, setChannels] = useState<SportEvent['channels']>([]);
  const matchId = data?.match.id;
  const ended = data?.match.state === 'post';
  useEffect(() => {
    if (!matchId || ended) return;
    void broadcastsFor(matchId).then(setChannels);
  }, [matchId, ended]);
  if (error && !data) return <main className="page"><p className="placeholder-row error">{error}</p></main>;
  if (!data) return <main className="page"><div className="row-skeleton" style={{ margin: 'var(--gutter)' }} /></main>;
  const m = data.match;
  const tabs: ['events' | 'stats' | 'lineups', string, boolean][] = [
    ['events', 'Verloop', data.events.length > 0], ['stats', 'Statistieken', data.stats.length > 0], ['lineups', 'Opstellingen', data.lineups.some((l) => l.starters.length)],
  ];
  const shown = tabs.find(([id, , ok]) => id === tab && ok)?.[0] ?? tabs.find(([, , ok]) => ok)?.[0];
  return (
    <main className="page sc-page sc-match-page">
      <header className="sc-match-head">
        <a className="eyebrow" {...linkProps(tableHref(m.league))}>{m.leagueName}</a>
        <span className="subtle">{dayText(m.start)} · {hhmm(m.start)}{data.venue ? ` · ${data.venue}` : ''}{data.attendance ? ` · ${data.attendance.toLocaleString('nl-NL')} toeschouwers` : ''}</span>
      </header>
      <div className="sc-match-hero">
        <ScoreBoard m={m} />
        {(m.incidents.length > 0) && (
          <div className="sc-hero-goals">
            {(['home', 'away'] as const).map((side) => (
              <ul key={side} className={`sc-${side}`}>
                {m.incidents.filter((i) => i.side === side).map((i, k) => (
                  <li key={k}>{i.kind === 'red' ? '🟥' : '⚽'} {i.player} {i.minute}{i.kind === 'pen' ? ' (p)' : i.kind === 'own' ? ' (e.d.)' : ''}</li>
                ))}
              </ul>
            ))}
          </div>
        )}
        {m.sport === 'soccer' && m.state !== 'pre' && <Timeline d={data} />}
        {(data.form.home || data.form.away) && m.state === 'pre' && (
          <div className="sc-hero-form">
            <span>{[...data.form.home].map((r, i) => <b key={i} className={`sc-res sc-${r === 'W' ? 'w' : r === 'L' ? 'l' : 'd'}`}>{r === 'W' ? 'W' : r === 'L' ? 'V' : 'G'}</b>)}</span>
            <span className="subtle">Vorm</span>
            <span>{[...data.form.away].map((r, i) => <b key={i} className={`sc-res sc-${r === 'W' ? 'w' : r === 'L' ? 'l' : 'd'}`}>{r === 'W' ? 'W' : r === 'L' ? 'V' : 'G'}</b>)}</span>
          </div>
        )}
      </div>
      {channels.length > 0 && (
        <section className="sc-watch">
          <span className="sc-label">Kijk live</span>
          <div className="chip-wrap" data-focus-group>
            {channels.map((c) => (
              <a key={`${c.accountId}:${c.streamId}`} className="chip chip-channel" {...linkProps(playLink(c.accountId, c.streamId))}>
                <ChannelLogo src={c.logo} name={c.name} size={24} />{c.name}
              </a>
            ))}
          </div>
        </section>
      )}
      {shown && (
        <>
          <nav className="filterbar" data-focus-group>
            {tabs.filter(([, , ok]) => ok).map(([id, label]) => <button key={id} type="button" className="chip" aria-pressed={id === shown} onClick={() => setTab(id)}>{label}</button>)}
          </nav>
          {shown === 'events' && (
            <ol className="sc-events">
              {[...data.events].reverse().map((e, i) => e.kind === 'period' ? (
                <li key={i} className="sc-ev-period"><span>{e.text}</span></li>
              ) : (
                <li key={i} className={`sc-ev sc-ev-${e.side ?? 'none'} sc-ev-${e.kind}`}>
                  <span className="sc-ev-min">{e.minute}</span>
                  <span className="sc-ev-body">
                    <span className="sc-ev-icon" aria-hidden="true">{EVENT_ICON[e.kind]}</span>
                    <span><b>{e.player ?? e.text}</b>{e.extra ? <small> {e.extra}</small> : null}{e.kind !== 'sub' && e.player ? <small> · {e.text}</small> : null}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
          {shown === 'stats' && (
            <div className="sc-stats">
              {data.stats.map((s) => {
                const tot = s.homeN + s.awayN || 1;
                return (
                  <div key={s.label} className="sc-stat">
                    <span className="sc-stat-top"><b>{s.home}</b><span>{s.label}</span><b>{s.away}</b></span>
                    <span className="sc-stat-bar"><i className="sc-stat-h" style={{ width: `${(s.homeN / tot) * 100}%` }} /><i className="sc-stat-a" style={{ width: `${(s.awayN / tot) * 100}%` }} /></span>
                  </div>
                );
              })}
            </div>
          )}
          {shown === 'lineups' && (
            <div className="sc-lineups">
              {(['home', 'away'] as const).map((side) => {
                const l = data.lineups.find((x) => x.side === side);
                const team = side === 'home' ? m.home : m.away;
                if (!l) return null;
                return (
                  <section key={side} className="sc-lineup">
                    <h3><Crest logo={team.logo} name={team.short} size={24} />{team.short}{l.formation ? <small> · {l.formation}</small> : null}</h3>
                    <ul>{l.starters.map((p) => <li key={p.name}><span className="sc-num">{p.n}</span>{p.name}<small>{p.pos}</small>{p.subOut ? <em title="Gewisseld">⇣</em> : null}</li>)}</ul>
                    {l.bench.length > 0 && <><h4>Bank</h4><ul className="sc-bench">{l.bench.map((p) => <li key={p.name}><span className="sc-num">{p.n}</span>{p.name}<small>{p.pos}</small>{p.subIn ? <em title="Ingevallen">⇡</em> : null}</li>)}</ul></>}
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
      {!shown && m.state === 'pre' && <p className="placeholder-row">Opstellingen en verloop verschijnen rond de aftrap.</p>}
      <p className="sc-back"><a {...linkProps('/sportcenter')}>‹ Alle wedstrijden</a></p>
      <div className="footer-space" />
    </main>
  );
}
